import Component from '@glimmer/component';
import { modifier } from 'ember-modifier';
import { guidFor } from '@ember/object/internals';
import { concat } from '@ember/helper';
import FormField from './form-field';

// ---------------------------------------------------------------------------
// Textarea – reimagined multiline text input
// ---------------------------------------------------------------------------

/**
 * Size variants consistent with all reimagined form components.
 */
export type TextareaSize = 'sm' | 'md' | 'lg';

interface TextareaSignature {
  Args: {
    // --- Value ---
    /** Current textarea value */
    value?: unknown;
    /** Called on every keystroke with the new value */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- permissive callback contract: callers pass handlers typed with varied event types (InputEvent, Event, …)
    onChange?: (value: string, event?: any) => void;
    /** Legacy alias for onChange */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- permissive callback contract: callers pass handlers typed with varied event types
    onInput?: (value: string, event?: any) => void;
    /** Called when the textarea loses focus */
    onBlur?: () => void;

    // --- Presentation ---
    /** Label displayed above the textarea */
    label?: string;
    /** Hint text displayed below the textarea */
    hint?: string;
    /** Legacy alias for hint */
    description?: string;
    /** Placeholder text */
    placeholder?: string;
    /** Number of visible text rows @default 3 */
    rows?: number;
    /** Auto-resize to fit content @default false */
    autoResize?: boolean;
    /** Size variant @default "md" */
    size?: TextareaSize;

    // --- State ---
    /** Validation error messages */
    errors?: string[];
    /** Show required indicator on label */
    isRequired?: boolean;
    /** Disable the textarea */
    isDisabled?: boolean;
    /** Make the textarea readonly */
    isReadonly?: boolean;

    // --- Extras ---
    /** Max character length */
    maxlength?: number;
    /** HTML name attribute */
    name?: string;
    /** Legacy class API support */
    classes?: { base?: string };
  };
  Element: HTMLTextAreaElement;
}

// Padding + font come from the shared .app-input--{sm,md,lg} size scale.
// The .app-textarea modifier opts out of the fixed input height and turns
// on vertical resize.

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Reimagined Textarea — a zero-dependency multiline text input component.
 *
 * Controlled textarea that fires `@onChange` on every keystroke.
 * Works standalone with `@value`/`@onChange`/`@errors`, or as a field inside the
 * `<Form>` orchestrator (`tio-ui/components/form`).
 *
 * Supports optional auto-resize that grows the textarea to fit its content.
 *
 * @example Basic usage
 * ```gts
 * import Textarea from 'tio-ui/components/textarea';
 *
 * <Textarea
 *   @label="Notes"
 *   @value={{this.notes}}
 *   @onChange={{this.setNotes}}
 *   @placeholder="Enter your notes…"
 *   @rows={{5}}
 * />
 * ```
 *
 * @example Auto-resizing with errors
 * ```gts
 * <Textarea
 *   @label="Description"
 *   @value={{this.description}}
 *   @onChange={{this.setDescription}}
 *   @autoResize={{true}}
 *   @errors={{array "Description is required"}}
 *   @isRequired={{true}}
 * />
 * ```
 */
export default class Textarea extends Component<TextareaSignature> {
  private _fieldId = `reimagined-textarea-${guidFor(this)}`;

  // ---- Derived state --------------------------------------------------------

  private get size(): TextareaSize {
    return this.args.size ?? 'md';
  }

  private get rows(): number {
    return this.args.rows ?? 3;
  }

  private get hasErrors(): boolean {
    return Boolean(this.args.errors?.length);
  }

  private get hint(): string | undefined {
    return this.args.hint ?? this.args.description;
  }

  private get value(): string {
    if (typeof this.args.value === 'string') {
      return this.args.value;
    }

    if (typeof this.args.value === 'number') {
      return String(this.args.value);
    }

    if (this.args.value == null) {
      return '';
    }

    return String(this.args.value);
  }

  private get textareaClasses(): string {
    // Theme + size scale shared with Input via .app-input. The
    // .app-textarea modifier opts out of the fixed input height
    // and turns on user-resize (or `--auto-resize` for autoresizing).
    const classes = ['app-input', `app-input--${this.size}`, 'app-textarea'];
    if (this.args.autoResize) {
      classes.push('app-textarea--auto-resize');
    }
    if (this.hasErrors) {
      classes.push('app-input--has-error');
    }
    if (this.args.classes?.base) {
      classes.push(this.args.classes.base);
    }
    return classes.join(' ');
  }

  // ---- Auto-resize modifier -------------------------------------------------

  // Keeps the DOM value property in sync with @value.
  // Setting text content (>{{value}}</textarea>) only sets the defaultValue;
  // once the user has typed, the browser's .value property diverges and must
  // be updated directly for programmatic resets to take effect.
  syncValueModifier = modifier((element: HTMLTextAreaElement, [value]: [string | undefined]) => {
    const normalizedValue = value ?? '';

    if (element.value !== normalizedValue) {
      element.value = normalizedValue;
    }
  });

  // Takes @value so it re-runs on programmatic replacements (canned prompts,
  // resets), which syncValueModifier writes without firing an input event.
  autoResizeModifier = modifier((element: HTMLTextAreaElement, [_value]: [string | undefined]) => {
    if (!this.args.autoResize) return;

    const resize = () => {
      element.style.height = 'auto';
      element.style.height = `${element.scrollHeight}px`;
    };

    // Initial resize
    resize();

    element.addEventListener('input', resize);
    return () => {
      element.removeEventListener('input', resize);
    };
  });

  // ---- Event handlers -------------------------------------------------------

  handleInput = (event: Event) => {
    const textarea = event.target as HTMLTextAreaElement;
    this.args.onChange?.(textarea.value, event);
    this.args.onInput?.(textarea.value, event);
  };

  handleBlur = () => {
    this.args.onBlur?.();
  };

  // ---- Template -------------------------------------------------------------

  <template>
    <FormField
      @label={{@label}}
      @hint={{this.hint}}
      @errors={{@errors}}
      @isRequired={{@isRequired}}
      @isDisabled={{@isDisabled}}
      @size={{this.size}}
      @fieldId={{this._fieldId}}
      as |field|
    >
      <textarea
        data-test-textarea
        id={{field.fieldId}}
        name={{@name}}
        placeholder={{@placeholder}}
        rows={{this.rows}}
        disabled={{@isDisabled}}
        readonly={{@isReadonly}}
        required={{@isRequired}}
        maxlength={{@maxlength}}
        aria-invalid={{if field.hasErrors "true"}}
        aria-describedby={{if this.hint (concat field.fieldId "-hint")}}
        class={{this.textareaClasses}}
        {{on "input" this.handleInput}}
        {{on "blur" this.handleBlur}}
        {{this.syncValueModifier this.value}}
        {{this.autoResizeModifier this.value}}
        ...attributes
      >{{this.value}}</textarea>
    </FormField>
  </template>
}

export type { TextareaSignature };
